import { AlertTriangle, ArrowLeft, ArrowRight, ArrowRightLeft, ChevronDown, Radar, Share2, ShieldCheck, Trash2, UsersRound } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatDuration, formatRelative } from "../api";
import { useAuth } from "../auth";
import { providerFromString } from "../brands";
import { Button, DiagnosticRow, EmptyState, ErrorState, EvidenceChip, LoadingState, ProviderMark, RhythmChart, Status } from "../components";
import { describeExpectation, detectMissedRun, inferExpectedInterval } from "../../shared/monitoring";
import { buildRhythm } from "./loopRhythm";
import { NotesPanel, RunAcknowledgement, useLoopNotes } from "./NotesPanel";
import { DefinitionPanel, useLoopDefinitions } from "./DefinitionPanel";
import { DependenciesPanel, useLoopDependencies } from "./DependenciesPanel";
import { ShareCardModal } from "./ShareCardModal";
import { MoveLoopModal } from "./MoveLoopModal";
import { PerformanceByAgent, useLoopPlacements } from "./PerformanceByAgent";
import { plannedMoves, providerName, runsOnLines } from "./loopPlacements";
import {
  FOLD_SECTIONS,
  LOOP_DETAIL_SECTIONS,
  type FoldSection,
  type SectionId,
  activeSection,
  definitionSummary,
  dependenciesSummary,
  detailsSummary,
  notesSummary,
  readFoldState,
  reliabilityFact,
  resolveFoldOpen,
  runsFact,
  writeFoldState,
} from "./loopDetailSpine";
import "../styles/cockpit.css";
import { LoopFormModal, evidenceFor, homeLineFor, markProviderFor, presentedStatus, type Finding, type LoopModel, type RunModel, type SharingOverview, type Team } from "./LoopsPage";

/** Runs shown before the "Show all" row; the API returns up to 100. */
const RUNS_FOLD = 8;
const SECTION_ORDER: readonly SectionId[] = LOOP_DETAIL_SECTIONS.map((section) => section.id);

function formatAbsolute(value: string | null): string {
  if (!value) return "Not reported";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function formatExpected(value: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function formatDay(value: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(value));
}

/**
 * The inferred expectation for the Rhythm fact: interval, basis, and the next expected start.
 * Computed with the same shared functions the Worker uses for the missed-run finding.
 */
function describeRhythmExpectation(loop: LoopModel, runs: RunModel[]): string {
  const expectation = inferExpectedInterval({ cadence: loop.cadence, scheduleHint: loop.scheduleHint, runStartsIso: runs.map((run) => run.startedAt) });
  const described = describeExpectation(expectation);
  if (!described) return "no expectation inferred";
  const nowIso = new Date().toISOString();
  const next = detectMissedRun({ nowIso, lastRunStartIso: loop.performance.lastRunAt, intervalMs: expectation.intervalMs, weekdaysOnly: expectation.weekdaysOnly });
  if (!next.expectedByIso) return `${described} · no run start to count from yet`;
  return `${described} · ${next.expectedByIso > nowIso ? "next by" : "was expected by"} ${formatExpected(next.expectedByIso)}`;
}

function tokenSummary(run: RunModel): string {
  if (run.inputTokens == null && run.outputTokens == null) return "tokens —";
  const total = (run.inputTokens ?? 0) + (run.outputTokens ?? 0);
  return run.inputTokens == null || run.outputTokens == null ? `${total.toLocaleString()} tokens · partial` : `${total.toLocaleString()} tokens`;
}

function foldStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** One folded section's open state: what the user last chose on this browser, else the content-aware default. */
function useFold(section: FoldSection, hasContent: boolean): { open: boolean; set: (open: boolean) => void } {
  const [stored, setStored] = useState<boolean | null>(() => readFoldState(foldStorage(), section));
  const set = useCallback((open: boolean) => {
    setStored(open);
    writeFoldState(foldStorage(), section, open);
  }, [section]);
  return { open: resolveFoldOpen(stored, section, hasContent), set };
}

function SectionHead({ id, label, aside, children }: { id: SectionId; label: string; aside?: ReactNode; children?: ReactNode }) {
  return (
    <div className="loop-detail-section__head">
      <span className="loop-detail-section__eyebrow" id={`loop-section-${id}`}>{label}</span>
      {aside ? <span className="loop-detail-section__aside">{aside}</span> : null}
      {children}
    </div>
  );
}

/** A secondary section: a one-line summary row that opens the panel beneath it. */
function FoldSectionBlock({ id, label, summary, fold, children }: { id: FoldSection; label: string; summary: string; fold: { open: boolean; set: (open: boolean) => void }; children: ReactNode }) {
  const bodyId = `loop-section-${id}-body`;
  return (
    <section id={id} className={`loop-detail-section loop-detail-section--fold ${fold.open ? "is-open" : ""}`.trim()} aria-labelledby={`loop-section-${id}`}>
      <button type="button" className="loop-detail-fold" aria-expanded={fold.open} aria-controls={bodyId} onClick={() => fold.set(!fold.open)}>
        <span className="loop-detail-section__eyebrow" id={`loop-section-${id}`}>{label}</span>
        <span className="loop-detail-fold__summary">{summary}</span>
        <span className="loop-detail-fold__toggle">{fold.open ? "Hide" : "Show"} <ChevronDown size={14} aria-hidden="true" /></span>
      </button>
      {fold.open ? <div id={bodyId} className="loop-detail-section__body">{children}</div> : null}
    </section>
  );
}

export function LoopDetailPage() {
  const { loopId = "" } = useParams();
  const { activeOrganization } = useAuth();
  const organizationId = activeOrganization?.id;
  const [loop, setLoop] = useState<LoopModel | null>(null);
  const [runs, setRuns] = useState<RunModel[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [diagnosticsError, setDiagnosticsError] = useState("");
  const [visibleIn, setVisibleIn] = useState<string[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [sharingCard, setSharingCard] = useState(false);
  const [moving, setMoving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [allRuns, setAllRuns] = useState(false);
  const [active, setActive] = useState<SectionId>("runs");
  const navRef = useRef<HTMLElement>(null);
  const scrollLockUntil = useRef(0);
  const navigate = useNavigate();
  const loopNotes = useLoopNotes(organizationId, loopId);
  const loopDefinitions = useLoopDefinitions(organizationId, loopId);
  const loopDependencies = useLoopDependencies(organizationId, loopId);
  const loopPlacements = useLoopPlacements(organizationId, loopId);
  const notesFold = useFold("notes", loopNotes.notes.length > 0);
  const definitionFold = useFold("definition", loopDefinitions.definitions.length > 0);
  const dependenciesFold = useFold("dependencies", loopDependencies.groups.length > 0);
  const detailsFold = useFold("details", true);
  const folds: Record<FoldSection, { open: boolean; set: (open: boolean) => void }> = { notes: notesFold, definition: definitionFold, dependencies: dependenciesFold, details: detailsFold };

  useEffect(() => {
    if (!organizationId) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setDiagnosticsError("");
    setLoop(null);
    setAllRuns(false);
    void Promise.all([
      api<{ loop: LoopModel; runs: RunModel[] }>(`/api/app/loops/${loopId}`, { signal: controller.signal }, organizationId),
      api<{ teams: Team[] }>("/api/app/group", { signal: controller.signal }, organizationId),
      api<{ findings: Finding[] }>("/api/app/diagnostics", { signal: controller.signal }, organizationId)
        .then((result) => ({ result, error: "" }))
        .catch((caught: unknown) => ({ result: null, error: caught instanceof Error ? caught.message : "Diagnostics are unavailable." })),
      api<SharingOverview>("/api/app/sharing", { signal: controller.signal }, organizationId).catch(() => null),
    ]).then(([detail, group, diagnostics, sharing]) => {
      setLoop(detail.loop);
      setRuns(detail.runs);
      setTeams(group.teams);
      setFindings(diagnostics.result?.findings.filter((finding) => finding.loopId === loopId) ?? []);
      setDiagnosticsError(diagnostics.error);
      const share = sharing?.loops.find((candidate) => candidate.id === loopId);
      setVisibleIn(sharing && share ? share.sharedTo.map((id) => sharing.groups.find((candidate) => candidate.id === id)?.name ?? "a group") : sharing ? [] : null);
    }).catch((caught: unknown) => {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setError(caught instanceof Error ? caught.message : "This loop is unavailable.");
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [loopId, organizationId, refreshKey]);

  // The spine: mark the section under the nav as you scroll. Sections keep their element across folds, so one observer per load is enough.
  useEffect(() => {
    if (!loop || typeof IntersectionObserver === "undefined") return;
    const navBottom = Math.round(navRef.current?.getBoundingClientRect().bottom ?? 48);
    const visible = new Set<SectionId>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const id = entry.target.id as SectionId;
        if (entry.isIntersecting) visible.add(id);
        else visible.delete(id);
      }
      if (Date.now() < scrollLockUntil.current) return;
      setActive((current) => activeSection(SECTION_ORDER, visible, current));
    }, { rootMargin: `-${Math.max(navBottom, 0)}px 0px -55% 0px`, threshold: 0 });
    for (const id of SECTION_ORDER) {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [loop]);

  function jumpTo(id: SectionId) {
    const fold = (FOLD_SECTIONS as readonly string[]).includes(id) ? folds[id as FoldSection] : null;
    if (fold && !fold.open) fold.set(true);
    setActive(id);
    scrollLockUntil.current = Date.now() + 800;
    window.history.replaceState(null, "", `#${id}`);
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" }));
  }

  async function archiveLoop() {
    if (!loop || !organizationId || !window.confirm(`Archive ${loop.name}? Run history will be retained.`)) return;
    setArchiving(true);
    setActionError("");
    try {
      await api(`/api/app/loops/${loop.id}`, { method: "DELETE" }, organizationId);
      navigate("/app/loops");
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : "The loop could not be archived.");
    } finally {
      setArchiving(false);
    }
  }

  if (loading) return <div className="page cockpit-page cockpit-state-page"><LoadingState>Reading loop evidence…</LoadingState></div>;
  if (error || !loop) return <div className="page cockpit-page cockpit-state-page"><ErrorState action={<Button onClick={() => setRefreshKey((value) => value + 1)}>Try again</Button>}>{error || "This loop is unavailable."}</ErrorState></div>;

  const readOnly = loop.visibility.readOnly;
  const evidence = evidenceFor(loop);
  const reliability = reliabilityFact(loop.performance);
  const runCount = runsFact(loop.performance);
  const sourceName = loop.discovery?.source.name || loop.discovery?.source.provider || null;
  const rhythm = buildRhythm(runs);
  const rhythmTotals = rhythm.reduce((sum, day) => ({ runs: sum.runs + day.succeeded + day.failed + day.unresolved, succeeded: sum.succeeded + day.succeeded, failed: sum.failed + day.failed, unresolved: sum.unresolved + day.unresolved }), { runs: 0, succeeded: 0, failed: 0, unresolved: 0 });
  const rhythmSummary = `${rhythmTotals.runs} runs over seven days: ${rhythmTotals.succeeded} succeeded, ${rhythmTotals.failed} failed, and ${rhythmTotals.unresolved} remain unresolved or cancelled.`;
  const placementData = loopPlacements.data;
  const runsOn = placementData ? runsOnLines(placementData.current, placementData.observed.providers) : [];
  const planned = placementData ? plannedMoves(placementData.placements) : [];
  const currentProvider = runsOn[0]?.provider ?? providerFromString(loop.performance.lastRunProvider);
  const shownRuns = allRuns ? runs : runs.slice(0, RUNS_FOLD);
  const placementCount = placementData?.placements.length ?? 0;
  const performanceDays = placementData?.window.days ?? 90;
  const latestDefinition = loopDefinitions.definitions[0] ?? null;

  return (
    <div className="page cockpit-page loop-detail">
      <Link className="back-link cockpit-back-link" to="/app/loops"><ArrowLeft size={15} aria-hidden="true" /> All loops</Link>

      <header className="loop-detail-head">
        <div className="loop-detail-head__row">
          <ProviderMark provider={markProviderFor(loop)} size={36} className="loop-detail-head__mark" />
          <div className="loop-detail-head__name">
            <h1>{loop.name}</h1>
            <p>{homeLineFor(loop)}</p>
          </div>
          <div className="loop-detail-head__chips">
            <Status value={presentedStatus(loop.performance.lastRunStatus)} />
            <EvidenceChip stage={evidence.stage}>{evidence.label} · {evidence.detail}</EvidenceChip>
            {!readOnly && visibleIn !== null ? <Link className="loop-detail-head__visible" to="/app/people"><UsersRound size={12} aria-hidden="true" /> {visibleIn.length ? `Visible in ${visibleIn.join(", ")}` : "Only you"}</Link> : null}
          </div>
          <div className="loop-detail-head__actions">
            <Button variant="secondary" onClick={() => setSharingCard(true)}><Share2 size={14} aria-hidden="true" /> Share publicly</Button>
            {!readOnly ? <><Button variant="secondary" onClick={() => setMoving(true)}><ArrowRightLeft size={14} aria-hidden="true" /> Move to another agent</Button><Button variant="secondary" onClick={() => setEditing(true)}>Edit loop</Button><Button variant="danger" busy={archiving} onClick={() => void archiveLoop()}><Trash2 size={14} aria-hidden="true" /> Archive</Button></> : null}
          </div>
        </div>
        <dl className="loop-detail-facts" aria-label="Loop facts">
          <div className={`loop-detail-fact loop-detail-fact--${reliability.state}`}><dt>Reliability</dt><dd><strong>{reliability.value}</strong><small>{reliability.detail}</small></dd></div>
          <div className="loop-detail-fact"><dt>Runs</dt><dd><strong>{runCount.value}</strong><small>{runCount.detail}</small></dd></div>
          <div className="loop-detail-fact"><dt>Rhythm</dt><dd><strong>{loop.cadence}</strong><small>{describeRhythmExpectation(loop, runs)}</small></dd></div>
          <div className="loop-detail-fact loop-detail-fact--runs-on">
            <dt>Runs on</dt>
            <dd>
              {loopPlacements.loading ? (
                <><strong>—</strong><small>Reading placements…</small></>
              ) : loopPlacements.error ? (
                <><strong>—</strong><small>Placements unavailable</small></>
              ) : runsOn.length > 0 ? (
                runsOn.map((line) => {
                  const name = providerName(line.provider);
                  const agentName = line.agentName && line.agentName.trim().toLowerCase() !== name.toLowerCase() ? line.agentName : null;
                  return (
                    <span key={`${line.kind}-${line.provider}`} className="loop-runs-on__line">
                      <strong><ProviderMark provider={line.provider} size={16} /> {name}{agentName ? <span> · {agentName}</span> : null}</strong>
                      <small>{line.kind === "declared" ? (line.since ? `since ${formatDay(line.since)}` : "declared, start not recorded") : `observed · last run ${line.since ? formatRelative(line.since) : "—"}`}</small>
                    </span>
                  );
                })
              ) : (
                <><strong>No runs yet</strong><small>{planned.length ? "Nothing observed so far" : "Declared, not observed"}</small></>
              )}
              {planned.map((move) => <small key={move.id} className="loop-runs-on__planned">Moving to {providerName(move.provider)} · waiting for its first run</small>)}
            </dd>
          </div>
        </dl>
      </header>

      <nav ref={navRef} className="loop-detail-nav" aria-label="Loop sections">
        <ul>
          {LOOP_DETAIL_SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`} className={active === section.id ? "is-active" : undefined} aria-current={active === section.id ? "location" : undefined} onClick={(event) => { event.preventDefault(); jumpTo(section.id); }}>{section.label}</a>
            </li>
          ))}
        </ul>
      </nav>

      {actionError ? <div className="loop-action-error"><ErrorState>{actionError}</ErrorState></div> : null}

      <div className="loop-detail-sheet">
        <section id="runs" className="loop-detail-section" aria-labelledby="loop-section-runs">
          <SectionHead id="runs" label="Runs" aside={rhythmTotals.runs ? `${rhythmTotals.runs} ${rhythmTotals.runs === 1 ? "run" : "runs"} this week` : "No runs this week"}>
            <div className="cockpit-legend" aria-label="Chart legend"><span><i className="is-success" />Succeeded</span><span><i className="is-failed" />Failed</span><span><i className="is-neutral" />Open / other</span></div>
            <Link className="loop-detail-section__link" to="/app/settings#keys">Reporting guide <ArrowRight size={13} aria-hidden="true" /></Link>
          </SectionHead>
          <div className="cockpit-rhythm-panel loop-detail-rhythm">
            <RhythmChart points={rhythm.map((day) => ({ label: day.label, succeeded: day.succeeded, failed: day.failed, unresolved: day.unresolved }))} summary={rhythmSummary} />
            <p className="loop-detail-rhythm__note">Zero-run days remain in view. Volume only; this is not a trend claim.</p>
          </div>
          {runs.length > 0 ? (
            <>
              <ol className="loop-run-list" aria-label="Recent runs">
                {shownRuns.map((run) => (
                  <li key={run.id} className="loop-run">
                    <div className="loop-run__lead">
                      <Status value={presentedStatus(run.status)} />
                      <span className="loop-run__agent"><strong>{run.agentName || "Unnamed agent"}</strong>{!run.agentProvider ? <small>Provider not reported</small> : run.agentProvider.toLowerCase() !== (run.agentName ?? "").toLowerCase() ? <small>{run.agentProvider}</small> : null}</span>
                    </div>
                    <p className="loop-run__summary">{run.outcomeSummary || run.errorMessage || "No outcome summary was reported."}</p>
                    <span className="loop-run__facts"><span>{run.durationMs == null ? "runtime —" : formatDuration(run.durationMs)}</span><span>{tokenSummary(run)}</span><span>{run.costUsd == null ? "cost —" : `$${run.costUsd.toFixed(3)} reported`}</span><code title={run.externalId || undefined}>{run.externalId || "external ID not reported"}</code></span>
                    <time dateTime={run.startedAt}>{formatAbsolute(run.startedAt)}</time>
                    <RunAcknowledgement runId={run.id} status={run.status} notes={loopNotes.notes} onAcknowledge={(runId) => loopNotes.post({ kind: "ack", runId })} />
                  </li>
                ))}
              </ol>
              {runs.length > RUNS_FOLD ? (
                <button type="button" className="loop-detail-more" onClick={() => setAllRuns((value) => !value)} aria-expanded={allRuns}>
                  {allRuns ? `Show the latest ${RUNS_FOLD}` : `Show all ${runs.length} runs`} <ChevronDown size={14} aria-hidden="true" />
                </button>
              ) : null}
            </>
          ) : (
            <EmptyState eyebrow="Declared, not observed" title="No runs have reported yet">Connect this loop’s agent and reuse a stable external run ID at start and completion.</EmptyState>
          )}
        </section>

        <section id="performance" className="loop-detail-section" aria-labelledby="loop-section-performance">
          <SectionHead id="performance" label="Performance by agent" aside={placementData ? `last ${performanceDays} days · ${placementCount} ${placementCount === 1 ? "placement" : "placements"}` : `last ${performanceDays} days`} />
          <PerformanceByAgent state={loopPlacements} readOnly={readOnly} embedded />
        </section>

        <FoldSectionBlock id="notes" label="Notes" summary={loopNotes.loading ? "Reading notes…" : loopNotes.error ? "Notes unavailable" : notesSummary(loopNotes.notes)} fold={notesFold}>
          <NotesPanel state={loopNotes} embedded />
        </FoldSectionBlock>

        <FoldSectionBlock id="definition" label="Definition" summary={loopDefinitions.loading ? "Reading the definition…" : loopDefinitions.error ? "Definition unavailable" : definitionSummary(latestDefinition)} fold={definitionFold}>
          <DefinitionPanel state={loopDefinitions} readOnly={readOnly} originName={loop.visibility.originOrganizationName} embedded />
        </FoldSectionBlock>

        <FoldSectionBlock id="dependencies" label="Dependencies" summary={loopDependencies.loading ? "Reading dependencies…" : loopDependencies.error ? "Dependencies unavailable" : dependenciesSummary(loopDependencies.groups, currentProvider)} fold={dependenciesFold}>
          <DependenciesPanel state={loopDependencies} readOnly={readOnly} originName={loop.visibility.originOrganizationName} embedded />
        </FoldSectionBlock>

        <section id="diagnostics" className="loop-detail-section" aria-labelledby="loop-section-diagnostics">
          <SectionHead id="diagnostics" label="Diagnostics" aside={diagnosticsError ? "unavailable" : findings.length ? `${findings.length} ${findings.length === 1 ? "finding" : "findings"}` : "no findings"} />
          {diagnosticsError ? (
            <ErrorState action={<Button onClick={() => setRefreshKey((value) => value + 1)}>Try again</Button>}>Diagnostics unavailable: {diagnosticsError}</ErrorState>
          ) : findings.length > 0 ? (
            <div className="cockpit-ledger-list">{findings.map((finding) => <DiagnosticRow key={finding.id} icon={finding.severity === "high" ? <AlertTriangle size={16} aria-hidden="true" /> : <Radar size={16} aria-hidden="true" />} title={finding.title} meta={`${finding.severity} priority`} detail={finding.detail} />)}</div>
          ) : (
            <div className="cockpit-quiet loop-detail-quiet"><ShieldCheck size={20} aria-hidden="true" /><p>No evidence-backed findings for this loop.</p><small>Silence here means no current diagnostic rule fired; it does not prove success.</small></div>
          )}
          <p className="loop-detail-section__note"><strong>Reliability denominator.</strong> Only succeeded and failed runs count as resolved. Running, queued, cancelled, and unknown records remain visible but are excluded.</p>
        </section>

        <FoldSectionBlock id="details" label="Details" summary={detailsSummary({ sourceName, confidence: loop.discovery?.confidence, ownerName: loop.owner?.name })} fold={detailsFold}>
          <dl className="loop-detail-details">
            <div><dt>Purpose</dt><dd>{loop.description || "No description supplied."}</dd></div>
            <div><dt>Provenance</dt><dd>{sourceName ?? "Declared manually"}<small>{loop.discovery ? `${loop.discovery.kind || "Catalog source"} · ${loop.discovery.confidence || "confidence unrated"}` : "No catalog source attached"}</small></dd></div>
            <div><dt>Last catalog signal</dt><dd>{loop.discovery?.lastCatalogedAt ? formatRelative(loop.discovery.lastCatalogedAt) : "—"}<small>{loop.discovery?.lastCatalogedAt ? formatAbsolute(loop.discovery.lastCatalogedAt) : "Not reported"}</small></dd></div>
            <div><dt>Ownership</dt><dd>{loop.owner?.name || "Owner not assigned"}<small>{loop.team?.name || "No team"}{loop.tags.length ? ` · ${loop.tags.join(" · ")}` : " · No tags"}</small></dd></div>
            <div><dt>Schedule note</dt><dd>{loop.scheduleHint || "No schedule note supplied"}</dd></div>
            <div><dt>Average runtime</dt><dd>{formatDuration(loop.performance.averageDurationMs)}<small>{loop.performance.averageDurationMs == null ? "Source did not report runtime" : "Completed runs with known duration"}</small></dd></div>
          </dl>
          <p className={`loop-detail-section__note ${readOnly ? "loop-detail-section__note--shared" : ""}`.trim()}>
            <strong>{readOnly ? "Read-only shared evidence." : "Origin stays attached."}</strong>{" "}
            {readOnly ? `This workspace can read reported telemetry from ${loop.visibility.originOrganizationName}. Editing remains with the owner.` : loop.discovery ? `Catalog evidence came from ${sourceName}. Run outcomes remain separate evidence.` : "This loop was declared without a catalog source. A declaration alone does not prove execution."}
          </p>
        </FoldSectionBlock>
      </div>

      {!readOnly ? <LoopFormModal open={editing} teams={teams} loop={loop} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); setRefreshKey((value) => value + 1); }} /> : null}
      <ShareCardModal open={sharingCard} onClose={() => setSharingCard(false)} kind="loop" loops={[{ id: loop.id, name: loop.name, resolvedRuns: loop.performance.resolvedRuns }]} />
      {!readOnly ? <MoveLoopModal open={moving} onClose={() => setMoving(false)} loop={{ id: loop.id, name: loop.name }} currentProvider={currentProvider} onPlanned={loopPlacements.refresh} dependencies={loopDependencies.groups} /> : null}
    </div>
  );
}
